import type { JSX } from 'react'
import { useSocketStore } from '../../store/socketStore.js'

type QualityKey = 'GOOD' | 'WARNING' | 'POOR'

const QUALITY_CONFIG: Record<QualityKey, { color: string; icon: string; label: string }> = {
  GOOD: { color: 'text-green-500', icon: 'pi-signal', label: 'Good' },
  WARNING: { color: 'text-yellow-500', icon: 'pi-exclamation-triangle', label: 'Warning' },
  POOR: { color: 'text-red-500', icon: 'pi-times-circle', label: 'Poor' },
}

const DOT_COLOR: Record<QualityKey, string> = {
  GOOD: 'bg-green-500',
  WARNING: 'bg-yellow-500',
  POOR: 'bg-red-500',
}

export interface NetworkQualityMeterProps {
  compact?: boolean
}

export function NetworkQualityMeter(
  props: NetworkQualityMeterProps = {},
): JSX.Element | null {
  const { compact } = props
  const connected = useSocketStore((s) => s.connected)
  const quality = useSocketStore((s) => s.quality)
  const mos = useSocketStore((s) => s.mos)
  const packetLoss = useSocketStore((s) => s.packetLoss)

  if (!connected) {
    if (compact) {
      return (
        <span
          className="flex items-center text-gray-400"
          title="Offline"
          aria-label="Network quality Offline"
        >
          <span className="h-2 w-2 rounded-full bg-gray-400" />
        </span>
      )
    }
    return (
      <span className="flex items-center gap-1 text-gray-400 text-sm">
        <i className="pi pi-circle-fill text-xs" />
        Offline
      </span>
    )
  }

  if (!quality) return null

  const cfg = QUALITY_CONFIG[quality]
  const tooltip = `MOS: ${(mos ?? 0).toFixed(2)} | Loss: ${((packetLoss ?? 0) * 100).toFixed(1)}%`

  if (compact) {
    return (
      <span
        className="flex items-center"
        title={tooltip}
        aria-label={`Network quality ${cfg.label}`}
      >
        <span className={`h-2 w-2 rounded-full ${DOT_COLOR[quality]}`} />
      </span>
    )
  }

  return (
    <span
      className={`flex items-center gap-1 text-sm ${cfg.color}`}
      title={tooltip}
      aria-label={`Network quality ${cfg.label}`}
    >
      <i className={`pi ${cfg.icon} text-xs`} />
      {cfg.label}
    </span>
  )
}
